import { removeSurroundingChars } from '@maximizer/shared/util';
import { BASE64_KEY_TOKEN_BODY_PATTERN } from './entity-key.utils';

/**
 * Parses IQ-Boost markup blocks emitted by the LLM:
 *   (Maximizer: {...}) – reference links to Maximizer entities
 *   (CRM: {...})       – create-CRM-object actions
 *
 * Outlook owns its own copy of this parser so its prompt format can evolve
 * independently of the AI app.
 */
export class OutlookAiMarkupUtils {
  private static readonly REF_PATTERN = /\(([^()]+)\)|\{([^{}]+)\}/g;
  private static readonly MAXIMIZER_PATTERN =
    /\(Maximizer:([^)]*)\)|Maximizer:\s*\{([^}]+)\}/g;
  private static readonly CRM_PATTERN =
    /\(CRM:\s*(?:\{([^}]+)\}|\(([^)]*)\))\)/gi;
  // LLM prose can echo entity keys as legacy numeric aliases or raw base64
  // Octopus keys; none are user-meaningful.
  private static readonly ENTITY_KEY_STRIP_PATTERNS: RegExp[] = [
    /\(EntityKey(\d+),\s*EntityKey(\d+)\)/gi,
    /\(Entity Key:\s*(?:entityKey|k)?\d+\)/gi,
    /\(EntityKey:\s*entityKey\d+\)/gi,
    /\(\s*entityKey\d+\s*\)/gi,
    /\bEntityKey:\s*entityKey\d+\b/gi,
    /\bEntityKey\d+\b/gi,
    new RegExp(
      String.raw`\(\s*EntityKey:\s*${BASE64_KEY_TOKEN_BODY_PATTERN}\s*\)`,
      'gi',
    ),
    new RegExp(
      String.raw`(?<![A-Za-z0-9+/])EntityKey:\s*${BASE64_KEY_TOKEN_BODY_PATTERN}(?![A-Za-z0-9+/])`,
      'gi',
    ),
  ];

  private static readonly TAGGED_PAREN_BLOCK =
    /\(((?:<[^>]{1,256}>|[^()])*)\)/g;

  private static normalizeBlockTags(value: string): string {
    return value.replace(this.TAGGED_PAREN_BLOCK, (block) =>
      /Maximizer|CRM|entityKey/i.test(block)
        ? block.replace(/<[^>]{1,256}>/g, '')
        : block,
    );
  }

  static stripEntityKeyRefs(value: string): string {
    if (!value) return '';
    return this.ENTITY_KEY_STRIP_PATTERNS.reduce(
      (text, pattern) => text.replace(pattern, ''),
      this.normalizeBlockTags(value),
    );
  }

  static replaceMaximizerReferences(
    value: string,
    transform: (params: Record<string, string>) => string | null,
  ): string {
    if (!value) return '';

    return this.normalizeBlockTags(value).replace(
      this.MAXIMIZER_PATTERN,
      (_match: string, refs: string, singleRef: string) => {
        const parts: string[] = [];
        let refMatch: RegExpExecArray | null;
        this.REF_PATTERN.lastIndex = 0;
        let found = false;

        const content = refs ?? singleRef;

        while ((refMatch = this.REF_PATTERN.exec(content)) !== null) {
          found = true;
          const refContent = refMatch[1] ?? refMatch[2];
          const params = this.parseReferenceContent(refContent);
          const replacement = transform(params);
          if (replacement) parts.push(replacement);
        }

        if (!found) {
          const params = this.parseReferenceContent(content);
          const replacement = transform(params);
          if (replacement) parts.push(replacement);
        }

        return parts.join(', ');
      },
    );
  }

  static replaceCRMObjects(
    value: string,
    transform: (crmContent: string) => string | null,
  ): string {
    if (!value) return '';

    return this.normalizeBlockTags(value).replace(
      this.CRM_PATTERN,
      (_match: string, curly: string, parentheses: string) => {
        const content = curly ?? parentheses;
        const replacement = transform(content);
        return replacement ?? '';
      },
    );
  }

  static parseReferenceContent(content: string): Record<string, string> {
    const params: Record<string, string> = {};
    const parts = content.split(/,(?=\s*[A-Za-z_]\w*\s*[:=])/);

    for (const part of parts) {
      const trimmed = part.trim();
      const colonIndex = trimmed.indexOf(':');
      const equalsIndex = trimmed.indexOf('=');

      let key: string;
      let value: string;

      if (colonIndex !== -1) {
        key = trimmed.slice(0, colonIndex).trim();
        value = trimmed.slice(colonIndex + 1).trim();
      } else if (equalsIndex === -1) {
        continue;
      } else {
        key = trimmed.slice(0, equalsIndex).trim();
        value = trimmed.slice(equalsIndex + 1).trim();
      }

      value = removeSurroundingChars(value, '"', '"');
      value = removeSurroundingChars(value, "'", "'");
      value = value
        .replaceAll(String.raw`\"`, '"')
        .replaceAll(String.raw`\'`, "'");

      params[key] = value;
    }

    return params;
  }
}
